import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, CircularProgress } from "@mui/material";
import Typography from "@mui/material/Typography";
import ArrowIcon from "assets/icons/arrowIcon.svg";
import { makeStyles } from "tss-react/mui";
import ColourConstants from "helpers/colourConstants";
import AccordionSummary from "@mui/material/AccordionSummary";
import IOSSwitch from "components/Elements/IOSSwitch";
import { updateSiteDetails } from "services/clients/sites/siteDetails";
import { showError } from "../../../../../redux/common/actions";
import { useFeatureFlags } from "contexts/FeatureFlagsContext";
import { FEATURE_FLAGS } from "constants/FeatureFlags";

const useStyles = makeStyles()(() => ({
	logoAccordion: {
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	integrationImage: {
		backgroundColor: "grey",
		height: "full",
	},
	sectionHeadingIntegration: {
		fontFamily: "Roboto Condensed",
		fontWeight: "normal",
		fontSize: "15px",
		whiteSpace: "nowrap",
	},
	sectionHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "17px",
	},
	summary: {
		alignItems: "center",
		justifyContent: "space-between",
		marginRight: "10px !important",
		marginTop: 0,
		marginBottom: 0,
	},
	critical: {
		color: ColourConstants.red,
		margin: "0 !important",
	},
	criticalPara: {
		fontWeight: "600 !important",
		marginLeft: "5px",
	},
	detailContainer: {
		marginTop: "25px !important",
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
}));

function SAPAssetRule({
	accordianDetailsCss,
	noExpand,
	children,
	defaultExpanded = true,
	showAssetReferences,
	siteId,
	isLoading,
}) {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const [isMouseOverSwitch, setIsMouseOverSwitch] = useState(false);
	const [isUpdating, setIsUpdating] = useState(true);
	const [isActive, setIsActive] = useState(showAssetReferences);
	const { isEnabled } = useFeatureFlags();

	const onSAPAssetRuleUpdate = async (e) => {
		e.stopPropagation();
		setIsUpdating(true);
		try {
			const response = await updateSiteDetails(siteId, {
				op: "replace",
				label: "showAssetReferences",
				value: !isActive,
			});
			if (response.status) {
				setIsActive(response?.data?.showAssetReferences ?? !isActive);
			}
		} catch (error) {
			showError(t("message.failedToRegisterSapAssetRule"));
		} finally {
			setIsUpdating(false);
		}
	};

	useEffect(() => {
		setIsActive(showAssetReferences);
	}, [showAssetReferences]);
		
	return (
		<Accordion
			title={t("title.licenses")}
			className={classes.detailContainer}
			expanded={isExpanded}
			onChange={() => {
				if (isMouseOverSwitch) {
					setIsExpanded((prev) => prev);
				} else {
					setIsExpanded(!isExpanded);
				}
			}}
			style={{ padding: 0 }}
		>
			<AccordionSummary
				expandIcon={
					noExpand ? null : (
						<img
							alt="Expand icon"
							src={ArrowIcon}
							className={classes.expandIcon}
						/>
					)
				}
				aria-controls="panel1a-content"
				id="panel1a-header"
				classes={{ content: classes.summary }}
			>
				<div
					classes={classes.sapIntegrationTitle}
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<Typography className={classes.sectionHeading}>
						{t("title.sapAssetRule")}
					</Typography>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: "10px",
							alignItems: "center",
						}}
					>
						<div
							onMouseEnter={() => setIsMouseOverSwitch(true)}
							onMouseLeave={() => setIsMouseOverSwitch(false)}
						>
							<IOSSwitch
								color={true}
								onChange={onSAPAssetRuleUpdate}
								currentStatus={isActive}
								disabled={isUpdating}
							/>
						</div>
					</div>
				</div>
			</AccordionSummary>
			<AccordionDetails className={accordianDetailsCss}>
				{isLoading ? <CircularProgress /> : children}
			</AccordionDetails>
		</Accordion>
	);
}

export default SAPAssetRule;
